import { Controller } from '@hotwired/stimulus';

/**
 * Dropzone Controller
 *
 * Handles file and folder uploads via drag-and-drop or file/folder picker inputs.
 * Supports sequential upload with progress feedback, folder traversal via the
 * File System Access API (webkitGetAsEntry), and automatic folder_path metadata
 * for backend auto-classification.
 *
 * Targets:
 * - progress: Element where upload progress messages are displayed
 * - folderInput: The hidden input[type="file"][webkitdirectory] for folder selection
 *
 * Values:
 * - maxFileSize: Maximum file size in MB (default: 10)
 * - acceptedFiles: MIME type pattern for accepted files (default: "application/pdf")
 * - sessionId: The onboarding session ID used in the upload URL
 *
 * Actions:
 * - onFileSelect: Handles change events from the standard file input
 * - onFolderSelect: Handles change events from the folder input (webkitdirectory)
 *
 * @example
 * <div data-controller="dropzone"
 *      data-dropzone-max-file-size-value="10"
 *      data-dropzone-accepted-files-value="application/pdf"
 *      data-dropzone-session-id-value="42">
 *
 *   <div data-dropzone-target="progress" class="hidden"></div>
 *
 *   <!-- Standard multi-file picker -->
 *   <input type="file" multiple accept="application/pdf"
 *          data-action="change->dropzone#onFileSelect">
 *
 *   <!-- Folder picker -->
 *   <input type="file" webkitdirectory
 *          data-dropzone-target="folderInput"
 *          data-action="change->dropzone#onFolderSelect">
 * </div>
 */
export default class extends Controller {
  static targets = ['progress', 'folderInput', 'notification', 'dropzoneContent'];

  static values = {
    maxFileSize: Number,
    acceptedFiles: String,
    sessionId: Number,
    folderThreshold: { type: Number, default: 20 },
  };

  static _VARIANTS = {
    info: {
      container: 'bg-gradient-to-r from-info-50 to-primary-50 border-info-500',
      icon: 'text-info-600',
      title: 'text-info-900',
      message: 'text-info-700',
      dismiss: 'text-info-400 hover:text-info-600',
      role: 'status',
      svg: `<svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" /></svg>`,
    },
    warning: {
      container: 'bg-gradient-to-r from-warning-50 to-amber-50 border-warning-500',
      icon: 'text-warning-600',
      title: 'text-warning-900',
      message: 'text-warning-700',
      dismiss: 'text-warning-400 hover:text-warning-600',
      role: 'alert',
      svg: `<svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" /></svg>`,
    },
    danger: {
      container: 'bg-gradient-to-r from-danger-50 to-rose-50 border-danger-500',
      icon: 'text-danger-600',
      title: 'text-danger-900',
      message: 'text-danger-700',
      dismiss: 'text-danger-400 hover:text-danger-600',
      role: 'alert',
      svg: `<svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>`,
    },
  };

  /** Folder icon SVG (Heroicons outline) — used for folder warning (State A) */
  static _FOLDER_SVG = `<svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" /></svg>`;

  /** X-mark icon SVG (Heroicons outline) — used for dismiss button */
  static _XMARK_SVG = `<svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>`;

  connect() {
    this._boundOnDragOver = this._onDragOver.bind(this);
    this._boundOnDragLeave = this._onDragLeave.bind(this);
    this._boundOnDrop = this._onDrop.bind(this);

    this.element.addEventListener('dragover', this._boundOnDragOver);
    this.element.addEventListener('dragleave', this._boundOnDragLeave);
    this.element.addEventListener('drop', this._boundOnDrop);

    // Legacy support: bind change listener on any file input that is not the
    // folder input (for views that do not yet use data-action)
    this._fileInput = this.element.querySelector('input[type="file"]:not([webkitdirectory])');
    if (this._fileInput) {
      this._boundOnFileInputChange = this.onFileSelect.bind(this);
      this._fileInput.addEventListener('change', this._boundOnFileInputChange);
    }

    this._uploading = false;
  }

  disconnect() {
    this.element.removeEventListener('dragover', this._boundOnDragOver);
    this.element.removeEventListener('dragleave', this._boundOnDragLeave);
    this.element.removeEventListener('drop', this._boundOnDrop);

    if (this._fileInput && this._boundOnFileInputChange) {
      this._fileInput.removeEventListener('change', this._boundOnFileInputChange);
    }
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Handles file selection from the standard file picker (no folder).
   * Validates each file for size and type, then uploads sequentially.
   *
   * @param {Event} event - The change event from the file input
   */
  onFileSelect(event) {
    this._hideNotification();

    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const { valid, errors } = this._collectValidationErrors(files);

    if (errors.length > 0) {
      const errorList = this._renderErrorList(errors);
      const suffix =
        valid.length > 0
          ? `<p class="mt-2">${valid.length} fichier(s) valide(s) en cours d'upload.</p>`
          : '';
      this._showNotification(
        'danger',
        "Certains fichiers n'ont pas pu être ajoutés :",
        `${errorList}${suffix}`,
        { dismissible: true }
      );
    }

    if (valid.length === 0) return;

    const entries = valid.map((file) => ({ file, folderPath: null }));
    this._uploadAllSequentially(entries);
  }

  /**
   * Handles folder selection from the webkitdirectory input.
   * Filters to PDF files only, checks the folder threshold, and shows
   * a warning banner for large folders (State A). Preserves
   * webkitRelativePath for backend auto-classification.
   *
   * @param {Event} event - The change event from the folder input
   */
  onFolderSelect(event) {
    this._hideNotification();

    const allFiles = Array.from(event.target.files || []);
    if (allFiles.length === 0) return;

    // Extract folder name from first file's webkitRelativePath
    const folderName = this._extractFolderName(allFiles[0]?.webkitRelativePath);

    // Filter to PDF files only (silently skip non-PDF)
    const pdfFiles = allFiles.filter((file) => this._isPdf(file));

    if (pdfFiles.length === 0) {
      this._showNotification(
        'warning',
        '',
        'Aucun fichier PDF trouvé dans le dossier sélectionné.<br>Sélectionnez un dossier contenant des fichiers PDF.'
      );
      return;
    }

    const { valid, errors } = this._collectFolderValidationErrors(pdfFiles);

    if (valid.length === 0) {
      if (errors.length > 0) {
        this._showNotification(
          'danger',
          "Certains fichiers n'ont pas pu être ajoutés :",
          this._renderErrorList(errors)
        );
      }
      return;
    }

    const entries = valid.map((file) => ({
      file,
      folderPath: this._extractFolderPath(file.webkitRelativePath),
    }));

    // Show folder warning for large folders (threshold check takes priority)
    if (pdfFiles.length >= this.folderThresholdValue) {
      this._showFolderWarning(folderName, pdfFiles.length, entries);
      return;
    }

    // Show warning for skipped oversized files when below threshold
    if (errors.length > 0) {
      const errorList = this._renderErrorList(errors);
      this._showNotification(
        'warning',
        'Fichiers ignorés (trop volumineux) :',
        `${errorList}<p class="mt-2">${valid.length} fichier(s) valide(s) en cours d\u2019upload.</p>`,
        { dismissible: true }
      );
    }

    this._uploadAllSequentially(entries);
  }

  // ---------------------------------------------------------------------------
  // Drag and drop
  // ---------------------------------------------------------------------------

  /**
   * @param {DragEvent} event
   */
  _onDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    this.element.classList.add('border-primary-500', 'bg-primary-50');
  }

  /**
   * @param {DragEvent} event
   */
  _onDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();
    this.element.classList.remove('border-primary-500', 'bg-primary-50');
  }

  /**
   * Handles drop events. Uses DataTransferItem.webkitGetAsEntry() to detect
   * folders and recursively collect PDF files from directory trees. Falls back
   * to DataTransfer.files for flat file drops.
   *
   * @param {DragEvent} event
   */
  async _onDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    this.element.classList.remove('border-primary-500', 'bg-primary-50');

    if (this._uploading) return;

    this._hideNotification();

    const items = event.dataTransfer.items;
    let entries = [];
    let droppedFolderName = null;
    let folderPdfCount = 0;

    // Try the modern entry-based API to detect folders
    if (items && items.length > 0 && items[0].webkitGetAsEntry) {
      // Check if a directory was dropped (for folder warning logic)
      const firstEntry = items[0].webkitGetAsEntry();
      if (firstEntry && firstEntry.isDirectory) {
        droppedFolderName = firstEntry.name;
      }

      entries = await this._collectEntriesFromDataTransfer(items);
      folderPdfCount = entries.length;
    } else {
      // Fallback: plain file list (no folder support)
      const files = Array.from(event.dataTransfer.files || []);
      const { valid, errors } = this._collectValidationErrors(files);

      if (errors.length > 0) {
        const errorList = this._renderErrorList(errors);
        const suffix =
          valid.length > 0
            ? `<p class="mt-2">${valid.length} fichier(s) valide(s) en cours d'upload.</p>`
            : '';
        this._showNotification(
          'danger',
          "Certains fichiers n'ont pas pu être ajoutés :",
          `${errorList}${suffix}`,
          { dismissible: true }
        );
      }

      entries = valid.map((file) => ({ file, folderPath: null }));
    }

    if (entries.length === 0) return;

    // Show folder warning for drag-and-dropped directories with 20+ PDFs
    if (droppedFolderName && folderPdfCount >= this.folderThresholdValue) {
      this._showFolderWarning(droppedFolderName, folderPdfCount, entries);
      return;
    }

    this._uploadAllSequentially(entries);
  }

  // ---------------------------------------------------------------------------
  // Folder traversal
  // ---------------------------------------------------------------------------

  /**
   * Collects file entries from DataTransferItemList, recursively traversing
   * any directories. Only PDF files are included.
   *
   * @param {DataTransferItemList} items
   * @returns {Promise<Array<{file: File, folderPath: string|null}>>}
   */
  async _collectEntriesFromDataTransfer(items) {
    const results = [];

    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry();
      if (!entry) continue;

      if (entry.isFile) {
        const file = await this._fileFromEntry(entry);
        if (this._isPdf(file) && this._isWithinSizeLimit(file)) {
          results.push({ file, folderPath: null });
        }
      } else if (entry.isDirectory) {
        const dirFiles = await this._readDirectoryRecursively(entry, entry.name);
        results.push(...dirFiles);
      }
    }

    return results;
  }

  /**
   * Recursively reads all files from a directory entry.
   * Only includes PDF files within the size limit.
   *
   * @param {FileSystemDirectoryEntry} dirEntry
   * @param {string} basePath - Accumulated relative path for folder_path
   * @returns {Promise<Array<{file: File, folderPath: string}>>}
   */
  async _readDirectoryRecursively(dirEntry, basePath) {
    const results = [];
    const entries = await this._readAllDirectoryEntries(dirEntry);

    for (const entry of entries) {
      if (entry.isFile) {
        const file = await this._fileFromEntry(entry);
        if (this._isPdf(file) && this._isWithinSizeLimit(file)) {
          results.push({ file, folderPath: basePath });
        }
      } else if (entry.isDirectory) {
        const subPath = `${basePath}/${entry.name}`;
        const subFiles = await this._readDirectoryRecursively(entry, subPath);
        results.push(...subFiles);
      }
    }

    return results;
  }

  /**
   * Reads all entries from a directory, handling the batch-based reader API.
   * DirectoryReader.readEntries() may return results in batches, so we must
   * call it repeatedly until it returns an empty array.
   *
   * @param {FileSystemDirectoryEntry} dirEntry
   * @returns {Promise<FileSystemEntry[]>}
   */
  _readAllDirectoryEntries(dirEntry) {
    return new Promise((resolve, reject) => {
      const reader = dirEntry.createReader();
      const allEntries = [];

      const readBatch = () => {
        reader.readEntries(
          (entries) => {
            if (entries.length === 0) {
              resolve(allEntries);
            } else {
              allEntries.push(...entries);
              readBatch();
            }
          },
          (error) => reject(error)
        );
      };

      readBatch();
    });
  }

  /**
   * Converts a FileSystemFileEntry to a File object.
   *
   * @param {FileSystemFileEntry} fileEntry
   * @returns {Promise<File>}
   */
  _fileFromEntry(fileEntry) {
    return new Promise((resolve, reject) => {
      fileEntry.file(resolve, reject);
    });
  }

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  /**
   * Collects validation errors for files (size + type checks).
   * Returns valid files and error messages without calling alert().
   *
   * @param {File[]} files
   * @returns {{ valid: File[], errors: string[] }}
   */
  _collectValidationErrors(files) {
    const maxSize = this._maxSizeBytes;
    const maxMB = this.maxFileSizeValue || 10;
    const valid = [];
    const errors = [];

    for (const file of files) {
      if (file.size > maxSize) {
        errors.push(`${file.name} — trop volumineux (max ${maxMB} MB)`);
        continue;
      }

      if (!this._isPdf(file)) {
        errors.push(`${file.name} — format non supporté (PDF uniquement)`);
        continue;
      }

      valid.push(file);
    }

    return { valid, errors };
  }

  /**
   * Collects validation errors for folder files (size-only check).
   * Non-PDF files should already be filtered before calling this method.
   * Returns valid files and error messages without calling alert().
   *
   * @param {File[]} files
   * @returns {{ valid: File[], errors: string[] }}
   */
  _collectFolderValidationErrors(files) {
    const maxSize = this._maxSizeBytes;
    const maxMB = this.maxFileSizeValue || 10;
    const valid = [];
    const errors = [];

    for (const file of files) {
      if (file.size > maxSize) {
        errors.push(`${file.name} — trop volumineux (max ${maxMB} MB)`);
        continue;
      }

      valid.push(file);
    }

    return { valid, errors };
  }

  /**
   * @param {File} file
   * @returns {boolean}
   */
  _isPdf(file) {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  }

  /**
   * @param {File} file
   * @returns {boolean}
   */
  _isWithinSizeLimit(file) {
    return file.size <= this._maxSizeBytes;
  }

  /** @returns {number} Maximum file size in bytes */
  get _maxSizeBytes() {
    return (this.maxFileSizeValue || 10) * 1024 * 1024;
  }

  // ---------------------------------------------------------------------------
  // Upload
  // ---------------------------------------------------------------------------

  /**
   * Uploads all entries one at a time, showing progress. Reloads the page
   * once after all uploads complete.
   *
   * @param {Array<{file: File, folderPath: string|null}>} entries
   */
  async _uploadAllSequentially(entries) {
    if (this._uploading) return;
    this._uploading = true;

    const total = entries.length;
    let successCount = 0;
    let hasError = false;

    this._disableDropzone();

    for (let i = 0; i < entries.length; i++) {
      const { file, folderPath } = entries[i];
      this._showProgress(i + 1, total, file.name);

      try {
        await this._uploadFile(file, folderPath);
        successCount++;
      } catch (error) {
        console.error(`Erreur upload "${file.name}":`, error);
        hasError = true;
      }
    }

    this._uploading = false;

    if (hasError && successCount === 0) {
      // State E: total failure — danger notification, re-enable dropzone
      this._hideProgress();
      this._enableDropzone();
      this._showNotification(
        'danger',
        "Échec de l'upload",
        "Aucun fichier n'a pu être uploadé. Veuillez réessayer."
      );
      return;
    }

    if (hasError) {
      // State D: partial failure — warning notification, then reload
      this._showNotification(
        'warning',
        'Upload partiel',
        `${successCount}/${total} fichier(s) uploadé(s) avec succès. Certains fichiers ont échoué.`
      );
    }

    // Check for near-duplicate documents before reloading
    await this._checkDuplicates();

    // Single page reload after all uploads complete
    window.location.reload();
  }

  /**
   * Calls the server to detect near-duplicate documents (signed vs unsigned, etc.).
   * Shows a progress message while checking. Results are persisted server-side;
   * the page reload that follows will display "Doublon" badges.
   */
  async _checkDuplicates() {
    this._showProgress(0, 0, '');
    const el = this._getOrCreateProgressElement();
    el.textContent = 'V\u00e9rification des doublons en cours...';

    try {
      await fetch(`/onboarding/${this.sessionIdValue}/check_duplicates`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this._csrfToken,
        },
      });
    } catch (error) {
      console.error('Erreur verification doublons:', error);
    }
  }

  /**
   * Uploads a single file to the server.
   *
   * @param {File} file - The file to upload
   * @param {string|null} folderPath - Relative folder path for auto-classification
   * @returns {Promise<void>}
   */
  async _uploadFile(file, folderPath) {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('authenticity_token', this._csrfToken);

    if (folderPath) {
      formData.append('folder_path', folderPath);
    }

    const response = await fetch(`/onboarding/${this.sessionIdValue}/upload_documents`, {
      method: 'POST',
      body: formData,
      headers: {
        Accept: 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': this._csrfToken,
      },
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }

    // Consume the response body but do not act on it individually;
    // the page will reload once all uploads are done.
    await response.text();
  }

  // ---------------------------------------------------------------------------
  // UI state
  // ---------------------------------------------------------------------------

  /**
   * Shows upload progress in the progress target or as a fallback message
   * appended to the controller element.
   *
   * @param {number} current - 1-based index of the file currently uploading
   * @param {number} total - Total number of files to upload
   * @param {string} filename - Name of the file currently uploading
   */
  _showProgress(current, total, filename) {
    const el = this._getOrCreateProgressElement();

    el.textContent = '';
    const line1 = document.createElement('div');
    line1.textContent = `Upload ${current}/${total} en cours...`;
    const line2 = document.createElement('div');
    line2.textContent = filename;
    line2.classList.add('font-normal', 'text-primary-500');
    el.appendChild(line1);
    el.appendChild(line2);
    el.classList.remove('hidden');
  }

  _getOrCreateProgressElement() {
    if (this.hasProgressTarget) return this.progressTarget;

    let el = document.getElementById('upload-message');
    if (!el) {
      el = document.createElement('div');
      el.id = 'upload-message';
      el.classList.add('mt-4', 'text-sm', 'text-primary-600', 'font-medium');
      this.element.appendChild(el);
    }
    return el;
  }

  _hideProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.add('hidden');
      this.progressTarget.textContent = '';
    } else {
      const el = document.getElementById('upload-message');
      if (el) el.remove();
    }
  }

  _disableDropzone() {
    this.element.style.opacity = '0.5';
    this.element.style.pointerEvents = 'none';
  }

  _enableDropzone() {
    this.element.style.opacity = '1';
    this.element.style.pointerEvents = 'auto';
  }

  // ---------------------------------------------------------------------------
  // Notification system
  // ---------------------------------------------------------------------------

  /**
   * Renders an inline notification matching Ui::AlertComponent CSS structure.
   * All HTML is built from hardcoded CSS classes and SVGs — title is set via
   * textContent (auto-escaped by the DOM). WARNING: the message parameter is
   * injected via innerHTML and MUST only contain trusted HTML built by internal
   * methods (e.g. _renderErrorList). Never pass user-controlled strings into message.
   *
   * @param {'info'|'warning'|'danger'} variant - Notification variant
   * @param {string} title - Bold title text (can be empty string for message-only)
   * @param {string} message - Body HTML content (supports raw HTML for lists)
   * @param {Object} [options]
   * @param {Array<{label: string, variant: 'primary'|'secondary', callback: Function}>} [options.actions] - Action buttons
   * @param {boolean} [options.dismissible] - Show dismiss X button
   * @param {string} [options.iconSvg] - Custom icon SVG override
   */
  _showNotification(variant, title, message, options = {}) {
    if (!this.hasNotificationTarget) return;

    const v = this.constructor._VARIANTS[variant] || this.constructor._VARIANTS.info;
    const iconSvg = options.iconSvg || v.svg;

    const container = document.createElement('div');
    container.className = `border-l-4 rounded-lg shadow-sm p-4 max-w-5xl mx-auto ${v.container}`;
    container.setAttribute('role', v.role);

    const flex = document.createElement('div');
    flex.className = 'flex items-start gap-3';

    // Icon — trusted hardcoded SVG string from static _VARIANTS or _FOLDER_SVG
    const iconWrap = document.createElement('div');
    iconWrap.className = `flex-shrink-0 mt-0.5 ${v.icon}`;
    iconWrap.innerHTML = iconSvg; // SAFE: only hardcoded SVG constants from _VARIANTS
    flex.appendChild(iconWrap);

    // Content
    const content = document.createElement('div');
    content.className = 'flex-1';

    if (title) {
      const titleEl = document.createElement('p');
      titleEl.className = `text-sm font-semibold ${v.title}`;
      titleEl.textContent = title; // safe: textContent escapes
      content.appendChild(titleEl);
    }

    if (message) {
      const msgEl = document.createElement('div');
      msgEl.className = `${title ? 'text-xs mt-1' : 'text-sm'} ${v.message}`;
      // SAFE: message only comes from _renderErrorList (which escapes user input)
      // or hardcoded French strings in this controller
      msgEl.innerHTML = message;
      content.appendChild(msgEl);
    }

    // Action buttons
    if (options.actions && options.actions.length > 0) {
      const actionsWrap = document.createElement('div');
      actionsWrap.className = 'mt-3 flex gap-2';

      for (const action of options.actions) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className =
          action.variant === 'secondary'
            ? 'inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-neutral-700 bg-white border border-neutral-300 hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 cursor-pointer'
            : 'inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 cursor-pointer';
        btn.textContent = action.label;
        if (action.callback) {
          btn.addEventListener('click', action.callback, { once: true });
        }
        actionsWrap.appendChild(btn);
      }

      content.appendChild(actionsWrap);
    }

    flex.appendChild(content);

    // Dismiss X button
    if (options.dismissible) {
      const dismissBtn = document.createElement('button');
      dismissBtn.type = 'button';
      dismissBtn.setAttribute('aria-label', 'Fermer');
      dismissBtn.className = `flex-shrink-0 ${v.dismiss}`;
      // SAFE: _XMARK_SVG is a hardcoded static constant
      dismissBtn.innerHTML = this.constructor._XMARK_SVG;
      dismissBtn.addEventListener('click', () => this._hideNotification(), { once: true });
      flex.appendChild(dismissBtn);
    }

    container.appendChild(flex);

    this.notificationTarget.textContent = '';
    this.notificationTarget.appendChild(container);
    this.notificationTarget.classList.remove('hidden');

    // Scroll into view
    this.notificationTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * Hides and clears the notification target content.
   * Restores the dropzone content if it was hidden (State A).
   */
  _hideNotification() {
    if (!this.hasNotificationTarget) return;
    this.notificationTarget.classList.add('hidden');
    this.notificationTarget.textContent = '';

    // Restore dropzone content if hidden by folder warning (State A)
    if (this.hasDropzoneContentTarget) {
      this.dropzoneContentTarget.classList.remove('hidden');
    }
  }

  /**
   * Shows the folder warning banner (State A) for folders with many PDFs.
   * Hides the dropzone content (icon, buttons, helper text) and shows
   * an info notification with folder icon and action buttons.
   *
   * @param {string} folderName - Name of the selected folder
   * @param {number} pdfCount - Number of PDF files detected
   * @param {Array<{file: File, folderPath: string|null}>} entries - Validated entries to upload
   */
  _showFolderWarning(folderName, pdfCount, entries) {
    // State A: hide dropzone content
    if (this.hasDropzoneContentTarget) {
      this.dropzoneContentTarget.classList.add('hidden');
    }

    const displayName = folderName || 'Dossier';

    this._showNotification(
      'info',
      `Dossier \u201c${displayName}\u201d sélectionné (${pdfCount} PDF)`,
      'Ce volume de fichiers risque de rallonger fortement le temps d\u2019analyse.<br>Pour accélérer le traitement, veuillez importer uniquement un bordereau et une facture par marché.',
      {
        iconSvg: this.constructor._FOLDER_SVG,
        dismissible: false,
        actions: [
          {
            label: 'Continuer quand même',
            variant: 'primary',
            callback: () => {
              this._hideNotification();
              this._uploadAllSequentially(entries);
            },
          },
          {
            label: 'Annuler la sélection',
            variant: 'secondary',
            callback: () => {
              this._hideNotification();
              // Reset folder input value so same folder can be re-selected
              if (this.hasFolderInputTarget) {
                this.folderInputTarget.value = '';
              }
            },
          },
        ],
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** @returns {string} CSRF token from the meta tag */
  get _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || '';
  }

  /**
   * Extracts the top-level folder name from a webkitRelativePath.
   * For example, "MonDossier/sous-dossier/devis.pdf" returns "MonDossier".
   *
   * @param {string} relativePath - The webkitRelativePath from the File object
   * @returns {string|null} The top-level folder name, or null if unavailable
   */
  _extractFolderName(relativePath) {
    if (!relativePath) return null;
    const segments = relativePath.split('/');
    return segments.length > 1 ? segments[0] : null;
  }

  /**
   * Extracts the folder path from a webkitRelativePath, stripping the filename.
   * For example, "MonDossier/sous-dossier/devis.pdf" returns "MonDossier/sous-dossier".
   *
   * @param {string} relativePath - The webkitRelativePath from the File object
   * @returns {string|null} The folder portion, or null if no folder
   */
  _extractFolderPath(relativePath) {
    if (!relativePath) return null;

    const lastSlash = relativePath.lastIndexOf('/');
    if (lastSlash <= 0) return null;

    return relativePath.substring(0, lastSlash);
  }

  /**
   * Escapes HTML special characters to prevent XSS in user-provided strings.
   *
   * @param {string} str
   * @returns {string}
   */
  _escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Renders validation errors as an HTML bullet list.
   * Error strings must follow the format "filename — reason".
   *
   * @param {string[]} errors
   * @returns {string} HTML string with <ul> list
   */
  _renderErrorList(errors) {
    const items = errors
      .map((e) => {
        const dashIndex = e.indexOf(' — ');
        if (dashIndex === -1) return `<li>${this._escapeHtml(e)}</li>`;
        const name = e.substring(0, dashIndex);
        const reason = e.substring(dashIndex + 3);
        return `<li><strong>${this._escapeHtml(name)}</strong> — ${this._escapeHtml(reason)}</li>`;
      })
      .join('');
    return `<ul class="list-disc list-inside">${items}</ul>`;
  }
}
